import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  X,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { useIntake } from "../context/IntakeContext";
import { LogoMark } from "./Logo";
import { PaymentOptions } from "./PaymentOptions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISPUTE_TYPES = [
  "Unpaid invoice",
  "Refund dispute",
  "Rental deposit",
  "Contractor dispute",
  "Small debt",
  "Other",
];

const PLAN_DETAILS = {
  letter: { lookup: "send_the_letter", name: "Standard Package", price: "$39" },
  full_petty: { lookup: "go_full_petty", name: "Premium Package", price: "$79" },
};

const empty = {
  name: "",
  email: "",
  phone: "",
  dispute_type: "Unpaid invoice",
  amount: "",
  opponent: "",
  description: "",
  plan: "letter",
};

const inputCls =
  "w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-xl px-4 py-3 text-base text-[#111827] placeholder:text-[#a1a1aa] focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/30 transition";

export const IntakeForm = () => {
  const intake = useIntake();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (intake.isOpen) {
      setStep(0);
      setDone(null);
      setForm((f) => ({ ...empty, plan: intake.plan, dispute_type: f.dispute_type }));
    }
  }, [intake.isOpen, intake.plan]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const canNext =
    step === 0
      ? form.name.trim() && form.email.trim()
      : step === 1
        ? form.amount.trim() && form.opponent.trim() && form.description.trim()
        : true;

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/cases`, form);
      setDone(res.data);
      toast.success("Case received — an NLL Agent is on it.");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const stepTitles = ["Your details", "The dispute", "Pick your package"];

  return (
    <AnimatePresence>
      {intake.isOpen && (
        <motion.div
          data-testid="intake-form-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-[#111827]/60 backdrop-blur-sm p-0 sm:p-6"
          onClick={intake.close}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-lg bg-white rounded-t-[2rem] sm:rounded-[2rem] shadow-2xl max-h-[92vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white/95 backdrop-blur px-6 sm:px-8 pt-6 pb-4 border-b border-[#E5E7EB] flex items-center justify-between rounded-t-[2rem]">
              <div className="flex items-center gap-3">
                <LogoMark size={32} className="rounded-lg" />
                <div>
                  <p className="font-display font-semibold text-lg leading-tight">
                    {done ? "Case received" : "Start Your Case"}
                  </p>
                  {!done && (
                    <p className="text-xs text-[#71717a]">
                      Step {step + 1} of 3 — {stepTitles[step]}
                    </p>
                  )}
                </div>
              </div>
              <button
                data-testid="intake-close-button"
                onClick={intake.close}
                className="p-2 -mr-2 text-[#71717a] hover:text-[#111827]"
                aria-label="Close"
              >
                <X size={22} />
              </button>
            </div>

            <div className="px-6 sm:px-8 py-6">
              {done ? (
                <div data-testid="intake-success" className="text-center py-6">
                  <CheckCircle2
                    size={64}
                    className="text-[#2563EB] mx-auto mb-5"
                    strokeWidth={1.5}
                  />
                  <h3 className="font-display font-semibold text-2xl mb-2">
                    It&rsquo;s official you&rsquo;ve filed
                  </h3>
                  <p className="text-[#52525B] text-base leading-relaxed max-w-sm mx-auto">
                    Your case is in the queue. An NLL Agent will review it and
                    reach out at{" "}
                    <span className="font-semibold text-[#111827]">
                      {done.email}
                    </span>
                    .
                  </p>
                  <p className="mt-5 text-xs text-[#a1a1aa] uppercase tracking-[0.2em]">
                    Reference: {done.id.slice(0, 8)}
                  </p>
                  <div className="mt-7" data-testid="intake-pay-now-button">
                    <PaymentOptions
                      caseId={done.id}
                      lookupKey={PLAN_DETAILS[done.plan].lookup}
                      priceLabel={PLAN_DETAILS[done.plan].price}
                    />
                  </div>
                  <button
                    data-testid="intake-done-button"
                    onClick={intake.close}
                    className="mt-3 w-full text-sm font-semibold text-[#71717a] hover:text-[#111827] py-2 transition-colors"
                  >
                    Pay later — I&rsquo;ll wait for the email
                  </button>
                </div>
              ) : (
                <>
                  {step === 0 && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-1.5">
                          Full name *
                        </label>
                        <input
                          data-testid="intake-name-input"
                          className={inputCls}
                          placeholder="Jane Doe"
                          value={form.name}
                          onChange={set("name")}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1.5">
                          Email *
                        </label>
                        <input
                          data-testid="intake-email-input"
                          type="email"
                          className={inputCls}
                          placeholder="jane@example.com"
                          value={form.email}
                          onChange={set("email")}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1.5">
                          Phone (optional)
                        </label>
                        <input
                          data-testid="intake-phone-input"
                          className={inputCls}
                          placeholder="(555) 123-4567"
                          value={form.phone}
                          onChange={set("phone")}
                        />
                      </div>
                    </div>
                  )}

                  {step === 1 && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          What kind of dispute? *
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {DISPUTE_TYPES.map((t) => (
                            <button
                              key={t}
                              data-testid={`dispute-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
                              onClick={() =>
                                setForm((f) => ({ ...f, dispute_type: t }))
                              }
                              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                                form.dispute_type === t
                                  ? "bg-[#111827] text-[#93C5FD] border-[#111827]"
                                  : "bg-[#FFFFFF] text-[#52525B] border-[#E5E7EB] hover:border-[#111827]"
                              }`}
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1.5">
                          Amount owed *
                        </label>
                        <input
                          data-testid="intake-amount-input"
                          className={inputCls}
                          placeholder="$450"
                          value={form.amount}
                          onChange={set("amount")}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1.5">
                          Who owes you? *
                        </label>
                        <input
                          data-testid="intake-opponent-input"
                          className={inputCls}
                          placeholder="Person or company name"
                          value={form.opponent}
                          onChange={set("opponent")}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1.5">
                          What happened? *
                        </label>
                        <textarea
                          data-testid="intake-description-input"
                          rows={4}
                          className={`${inputCls} resize-none`}
                          placeholder="Plain English is fine. Dates, amounts, and what they promised help a lot."
                          value={form.description}
                          onChange={set("description")}
                        />
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-3">
                      {[
                        {
                          id: "letter",
                          name: "Standard Package",
                          price: "$39",
                          desc: "Certified demand letter, 14-day deadline, court-ready PDF.",
                          testid: "plan-option-letter",
                        },
                        {
                          id: "full_petty",
                          name: "Premium Package",
                          price: "$79",
                          desc: "Letter + Voice of Justice calls, follow-ups, Final Notice, tap-to-file.",
                          testid: "plan-option-full",
                        },
                      ].map((p) => (
                        <button
                          key={p.id}
                          data-testid={p.testid}
                          onClick={() =>
                            setForm((f) => ({ ...f, plan: p.id }))
                          }
                          className={`w-full text-left rounded-2xl border-2 p-5 transition-colors ${
                            form.plan === p.id
                              ? "border-[#2563EB] bg-[#2563EB]/10"
                              : "border-[#E5E7EB] bg-[#FFFFFF] hover:border-[#a1a1aa]"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-display font-semibold text-xl">
                              {p.name}
                            </span>
                            <span className="font-display font-semibold text-2xl">
                              {p.price}
                            </span>
                          </div>
                          <p className="text-sm text-[#52525B] mt-1">
                            {p.desc}
                          </p>
                        </button>
                      ))}
                      <p className="text-xs text-[#a1a1aa] pt-1">
                        You only pay when your letter is ready to send.
                      </p>
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-8">
                    {step > 0 ? (
                      <button
                        data-testid="intake-back-button"
                        onClick={() => setStep((s) => s - 1)}
                        className="flex items-center gap-2 text-sm font-semibold text-[#52525B] hover:text-[#111827] transition-colors"
                      >
                        <ArrowLeft size={16} /> Back
                      </button>
                    ) : (
                      <span />
                    )}
                    {step < 2 ? (
                      <button
                        data-testid="intake-next-button"
                        disabled={!canNext}
                        onClick={() => setStep((s) => s + 1)}
                        className="flex items-center gap-2 bg-[#111827] text-[#FFFFFF] font-semibold px-6 py-3 rounded-full hover:bg-[#1F2937] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Continue <ArrowRight size={16} />
                      </button>
                    ) : (
                      <button
                        data-testid="intake-form-submit-button"
                        disabled={submitting}
                        onClick={submit}
                        className="flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold px-6 py-3 rounded-full transition-colors disabled:opacity-60"
                      >
                        {submitting ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            Filing...
                          </>
                        ) : (
                          <>
                            Submit My Case <ArrowRight size={16} />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
